import type { BrowseNode, ConnectorResponse, SourceDocument, SyncRun } from '../types/api'
import { apiClient } from './client'

// ── List / Create ──────────────────────────────────────────────────

export async function getConnectors(projectId: number): Promise<ConnectorResponse[]> {
  return apiClient.get<ConnectorResponse[]>(`/projects/${projectId}/connectors`)
}

export async function createConnector(
  projectId: number,
  params: {
    provider: string
    displayName: string
    credentials?: string
    configJson?: string
  }
): Promise<ConnectorResponse> {
  return apiClient.post<ConnectorResponse>(`/projects/${projectId}/connectors`, params)
}

// ── Single connector ───────────────────────────────────────────────

export async function getConnector(connectorId: number): Promise<ConnectorResponse> {
  return apiClient.get<ConnectorResponse>(`/connectors/${connectorId}`)
}

export async function updateConnector(
  connectorId: number,
  params: { displayName?: string; configJson?: string }
): Promise<ConnectorResponse> {
  return apiClient.patch<ConnectorResponse>(`/connectors/${connectorId}`, params)
}

export async function deleteConnector(
  connectorId: number,
  documentPolicy?: 'delete' | 'retain' | 'detach'
): Promise<{ deleted: boolean; documentPolicy: string }> {
  const query = documentPolicy ? `?documentPolicy=${documentPolicy}` : ''
  return apiClient.delete(`/connectors/${connectorId}${query}`)
}

export async function rotateCredentials(
  connectorId: number,
  credentials: string
): Promise<{ rotated: boolean }> {
  return apiClient.patch(`/connectors/${connectorId}/credentials`, { credentials })
}

// ── Browse ─────────────────────────────────────────────────────────

export async function browseConnector(
  connectorId: number,
  parentId?: string | null
): Promise<BrowseNode[]> {
  const query = parentId ? `?parentId=${encodeURIComponent(parentId)}` : ''
  return apiClient.get<BrowseNode[]>(`/connectors/${connectorId}/browse${query}`)
}

// ── GitHub discover (pre-create) ───────────────────────────────────

export interface GitHubRepoSummary {
  fullName: string
  owner: string
  name: string
  defaultBranch: string
  private: boolean
  description: string | null
}

export interface GitHubRepoDiscoveryResult {
  repos: GitHubRepoSummary[]
  orgs: string[]
  warnings: string[]
}

export async function listGitHubReposForSetup(
  projectId: number,
  credentials: string,
  org?: string
): Promise<GitHubRepoDiscoveryResult> {
  return apiClient.post<GitHubRepoDiscoveryResult>(
    `/projects/${projectId}/connectors/github/repos`,
    { credentials, ...(org ? { org } : {}) }
  )
}

export async function listGitHubBranchesForSetup(
  projectId: number,
  credentials: string,
  owner: string,
  repo: string
): Promise<string[]> {
  const result = await apiClient.post<{ branches: string[] }>(
    `/projects/${projectId}/connectors/github/branches`,
    { credentials, owner, repo }
  )
  return result.branches
}

// ── Scopes ─────────────────────────────────────────────────────────

export async function setConnectorScopes(
  connectorId: number,
  scopes: Array<{ externalIdentifier: string; scopeType: string; displayName?: string }>
): Promise<{ saved: boolean }> {
  return apiClient.put(`/connectors/${connectorId}/scopes`, { scopes })
}

// ── Sync controls ──────────────────────────────────────────────────

export async function triggerSync(connectorId: number): Promise<{ enqueued: boolean }> {
  return apiClient.post(`/connectors/${connectorId}/sync`)
}

export async function pauseConnector(connectorId: number): Promise<{ paused: boolean }> {
  return apiClient.post(`/connectors/${connectorId}/pause`)
}

export async function resumeConnector(connectorId: number): Promise<{ resumed: boolean }> {
  return apiClient.post(`/connectors/${connectorId}/resume`)
}

// ── Sync runs ──────────────────────────────────────────────────────

export async function getSyncRuns(connectorId: number): Promise<SyncRun[]> {
  return apiClient.get<SyncRun[]>(`/connectors/${connectorId}/runs`)
}

// ── Source documents ────────────────────────────────────────────────

export async function getSourceDocuments(connectorId: number): Promise<SourceDocument[]> {
  return apiClient.get<SourceDocument[]>(`/connectors/${connectorId}/documents`)
}
